import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

import { Button, Modal } from '@Sage/hoi-poi-ui';
import { EntityCrudActions, EntityDetailActions, EntityActions } from 'actions';
import { ensureRoute } from 'utils/routes';
import { getLiteral } from 'utils/getLiteral';
import { isEqual } from 'utils/objects';
import { getActiveCrudName } from 'utils/crud';

import UsageLimitInfo from 'containers/components/UsageLimitInfo';
import MergeFilledIcon from 'components/SvgIcons/MergeFilled';
import TooltipHoi from 'components/TooltipHoi';
import { ButtonHeader, ButtonHeaderGrey, ButtonHeaderWhite } from 'components/buttons';

const propTypes = {
    loadingSave: PropTypes.bool,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    title: PropTypes.string,
    saveText: PropTypes.string,
    cancelText: PropTypes.string,
    saveButtonLight: PropTypes.bool,
    hideSave: PropTypes.bool,
    salesOrderType: PropTypes.string,
};

const mapStateToProps = (state) => {
    const activeCrudName = getActiveCrudName(state);
    const activeCrud = state.entityCrud?.[activeCrudName] || {};
    const { data = [], fieldsToClear = {}, duplicateNoChanges, originalData } = activeCrud;
    const { isConfirmModalOpen, isExtraFieldsModalOpen, urlToOpen, queryString } = state.entityCrud;
    const { nextDetailEntityType, nextDetail } = state.entityDetail;
    const salesOrderType = data?.salesOrderTypeDescription;

    const hasEditedFields = data && Object.keys(data).some((key) => data[key]);
    const hasFieldsToClear = Object.keys(fieldsToClear).length > 0;
    const isEmptyCrud = !hasEditedFields && !hasFieldsToClear;

    return {
        isConfirmModalOpen,
        isExtraFieldsModalOpen,
        urlToOpen,
        queryString,
        nextDetailEntityType,
        nextDetail,
        originalData,
        data,
        isEmptyCrud,
        duplicateNoChanges,
        salesOrderType,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        toggleConfirmModal: bindActionCreators(EntityCrudActions, dispatch).toggleConfirmModal,
        toggleExtraFieldsModal: bindActionCreators(EntityCrudActions, dispatch)
            .toggleExtraFieldsModal,
        resetNextDetail: bindActionCreators(EntityDetailActions, dispatch).resetNextDetail,
        openMergeEntityDialog: bindActionCreators(EntityActions, dispatch).openMergeEntityDialog,
    };
};

@connect(mapStateToProps, mapDispatchToProps)
class EntityCrudHeader extends PureComponent {
    onSave = (event) => {
        const { loadingSave, onSave } = this.props;

        if (loadingSave) return Promise.resolve();
        return onSave()?.catch(console.error);
    };

    onConfirm = () => {
        const { toggleConfirmModal, nextDetail, nextDetailEntityType, resetNextDetail } =
            this.props;

        resetNextDetail();
        this.props.onCancel();
        if (nextDetail) {
            const isMapOpen = window.location.href.includes('/map/');
            ensureRoute(
                isMapOpen
                    ? `${nextDetailEntityType.entity}/map/${nextDetail.Id}`
                    : `/${nextDetailEntityType.entity}/${nextDetail.Id}`,
            );
        }
        toggleConfirmModal(false);
    };

    goToSettings = () => {
        const { urlToOpen, queryString } = this.props;
        ensureRoute(urlToOpen, queryString);
    };

    checkChanges = () => {
        const { originalData, data, toggleConfirmModal } = this.props;
        isEqual(originalData, data, true) ? this.onConfirm() : toggleConfirmModal(true);
    };

    onClose = () => {
        const { toggleConfirmModal, resetNextDetail, toggleExtraFieldsModal } = this.props;
        resetNextDetail();
        toggleExtraFieldsModal(false);
        toggleConfirmModal(false);
    };

    onMerge = () => {
        const { id, openMergeEntityDialog } = this.props;
        openMergeEntityDialog(id);
    };

    render() {
        const {
            loading,
            loadingSave,
            isEmptyCrud,
            title,
            salesOrderType,
            cancelText,
            saveText,
            saveButtonLight,
            isConfirmModalOpen,
            hideSave,
            isReadOnly,
            canMerge,
            id,
            version,
            duplicateNoChanges,
            isExtraFieldsModalOpen,
            entity,
        } = this.props;

        let SaveButton = saveButtonLight ? ButtonHeaderGrey : ButtonHeader;
        const disableSave = isEmptyCrud || loadingSave || isReadOnly || duplicateNoChanges;
        const showMerge = canMerge && id;
        return (
            <div className="entity-crud-header">
                <div className="entity-crud-title">
                {loading ? null : salesOrderType ? `${title} ${salesOrderType}` : title}

                    <UsageLimitInfo entity={entity} />
                </div>
                <div className="entity-crud-buttons">
                    {showMerge && (
                        <TooltipHoi placement="bottom" content={getLiteral('action_merge')}>
                            <div onClick={this.onMerge} className="entity-crud-buttons__merge">
                                <MergeFilledIcon />
                            </div>
                        </TooltipHoi>
                    )}
                    {version !== 2 && (
                        <ButtonHeaderWhite
                            onClick={this.checkChanges}
                            classes={['fm-button-header__cancel']}
                        >
                            {cancelText}
                        </ButtonHeaderWhite>
                    )}
                    {!hideSave && version !== 2 && (
                        <SaveButton
                            onClick={this.onSave}
                            classes={['fm-button-header__save']}
                            disabled={disableSave}
                        >
                            {saveText}
                        </SaveButton>
                    )}
                    {version === 2 && (
                        <Button
                            type="terciary"
                            onClick={this.checkChanges}
                            className="fm-button-header__cancel"
                        >
                            {cancelText}
                        </Button>
                    )}
                    {!hideSave && version === 2 && (
                        <Button
                            onClick={this.onSave}
                            className="fm-button-header__save"
                            isDisabled={disableSave}
                        >
                            {saveText}
                        </Button>
                    )}
                </div>
                <Modal
                    overlayClassName="entity-crud-header__overlay-cancel"
                    useCornerClose={false}
                    isOpen={isConfirmModalOpen || isExtraFieldsModalOpen}
                    onCancel={this.onClose}
                    onConfirm={isConfirmModalOpen ? this.onConfirm : this.goToSettings}
                    confirmText={getLiteral('action_discard_changes')}
                    cancelText={getLiteral('action_cancel')}
                    title={getLiteral('label_discard_unsaved_changes')}
                    size="small"
                ></Modal>
            </div>
        );
    }
}

EntityCrudHeader.propTypes = propTypes;

export default EntityCrudHeader;
